import {
  MermaidElementModel,
  replaceMermaidSource,
  type RootBlockModel,
} from '@blocksuite/affine-model';
import {
  MermaidActionProvider,
  translate,
} from '@blocksuite/affine-shared/services';
import { WidgetComponent, WidgetViewExtension } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import type { ShapeId } from '@visimer/core';
import { css, html, nothing } from 'lit';
import { query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { literal, unsafeStatic } from 'lit/static-html.js';

import {
  getMermaidViewModeSignal,
  getMermaidVirtualSelectionSignal,
  getMermaidVisualEditingSignal,
  isMermaidInlineEditableEntityId,
  setMermaidAddPicker,
  setMermaidConnectSource,
  setMermaidInlineEditing,
  setMermaidViewMode,
  setMermaidVirtualSelection,
  showMermaidDiagram,
} from '../editor-state.js';
import type {
  hitTestMermaidInteractionSnapshot,
  MermaidInteractionEntityGeometry,
  MermaidInteractionSnapshot,
  mermaidSvgPointToElementLocal,
} from '../renderer/interaction-geometry.js';
import { getMermaidRenderStateSignal } from '../renderer/state.js';
import { trackMermaidAction } from '../telemetry.js';
import type { MermaidFamilyEditingController } from './family-editing.js';
import type { MermaidFlowchartEditingController } from './flowchart-editing.js';
import {
  applyMermaidSourceInput,
  getMermaidErrorLocation,
  type MermaidTextDeltaOperation,
  transformMermaidTextIndex,
} from './source-sync.js';
import { clampMermaidOverlayPosition } from './visual-position.js';

export const MERMAID_EDITOR_WIDGET = 'affine-mermaid-editor-widget';

const INLINE_COMMIT_DEBOUNCE_MS = 450;

const tr = (widget: MermaidEditorWidget, source: string) =>
  translate(widget.std, source) ?? source;

function stopEvent(event: Event) {
  event.stopPropagation();
}

export class MermaidEditorWidget extends WidgetComponent<RootBlockModel> {
  static override styles = css`
    :host {
      position: absolute;
      inset: 0;
      pointer-events: none !important;
      z-index: calc(var(--affine-z-index-popover, 10) - 2);
    }

    .editor-surface {
      position: absolute;
      display: grid;
      grid-template-rows: auto minmax(0, 1fr) auto;
      box-sizing: border-box;
      overflow: hidden;
      border: 1px solid var(--affine-border-color);
      border-radius: 8px;
      background: var(--affine-background-primary-color);
      box-shadow: var(--affine-shadow-2);
      pointer-events: auto;
    }

    .header {
      display: flex;
      min-height: 36px;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 4px 6px;
      border-bottom: 1px solid var(--affine-border-color);
      background: var(--affine-background-overlay-panel-color);
    }

    .modes,
    .recovery {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    button {
      min-height: 28px;
      padding: 4px 8px;
      border: 0;
      border-radius: 5px;
      background: transparent;
      color: inherit;
      font: inherit;
      cursor: pointer;
    }

    button:hover,
    button[aria-pressed='true'] {
      background: var(--affine-hover-color);
    }

    button:focus-visible,
    textarea:focus-visible {
      outline: 2px solid var(--affine-primary-color);
      outline-offset: -2px;
    }

    .visual-layer {
      position: absolute;
      inset: 0;
      pointer-events: none;
    }

    .visual-hit-area {
      position: absolute;
      pointer-events: auto;
      background: transparent;
    }

    .virtual-selection {
      position: absolute;
      box-sizing: border-box;
      border: 2px solid var(--affine-primary-color);
      border-radius: 6px;
      pointer-events: none;
    }

    .visual-toolbar,
    .add-picker {
      position: absolute;
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px;
      border: 1px solid var(--affine-border-color);
      border-radius: 8px;
      background: var(--affine-background-overlay-panel-color);
      box-shadow: var(--affine-shadow-2);
      pointer-events: auto;
      white-space: nowrap;
    }

    .visual-toolbar select,
    .visual-toolbar input {
      min-height: 28px;
      max-width: 132px;
      border: 0;
      border-radius: 5px;
      background: var(--affine-background-primary-color);
      color: inherit;
      font: inherit;
    }

    .add-node-handle,
    .connect-handle {
      position: absolute;
      width: 24px;
      height: 24px;
      min-height: 24px;
      padding: 0;
      border: 1px solid var(--affine-border-color);
      border-radius: 50%;
      background: var(--affine-background-overlay-panel-color);
      box-shadow: var(--affine-shadow-1);
      pointer-events: auto;
      transform: translate(-50%, -50%);
      font-weight: 600;
    }

    .add-node-handle {
      transform: translate(-50%, 6px);
    }

    .connect-handle {
      cursor: crosshair;
    }

    .connect-target {
      position: absolute;
      box-sizing: border-box;
      border: 2px dashed var(--affine-primary-color);
      border-radius: 6px;
      pointer-events: none;
    }

    .connect-preview {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      overflow: visible;
      pointer-events: none;
    }

    .connect-preview line {
      stroke: var(--affine-primary-color);
      stroke-width: 2;
      stroke-dasharray: 5 4;
      vector-effect: non-scaling-stroke;
    }

    .inline-node-editor {
      position: absolute;
      box-sizing: border-box;
      resize: none;
      overflow: auto;
      padding: 4px 6px;
      border: 2px solid var(--affine-primary-color);
      border-radius: 6px;
      background: var(--affine-background-primary-color);
      color: inherit;
      font: inherit;
      line-height: 1.25;
      text-align: center;
      pointer-events: auto;
    }

    .inline-node-editor:focus-visible {
      outline: 2px solid var(--affine-primary-color);
      outline-offset: 1px;
    }

    .workspace {
      display: grid;
      min-width: 0;
      min-height: 0;
    }

    .workspace.code {
      grid-template-columns: minmax(0, 1fr);
    }

    .workspace.split {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }

    .code-pane,
    .preview {
      min-width: 0;
      min-height: 0;
    }

    .code-pane {
      display: flex;
      background: var(--affine-background-primary-color);
    }

    textarea {
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      resize: none;
      box-sizing: border-box;
      border: 0;
      padding: 12px;
      background: transparent;
      color: inherit;
      font-family: var(--affine-font-code-family);
      font-size: var(--affine-font-sm);
      line-height: 1.5;
      tab-size: 2;
      white-space: pre;
      overflow: auto;
    }

    .preview {
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-left: 1px solid var(--affine-border-color);
      background: var(--affine-background-primary-color);
    }

    .preview svg {
      display: block;
      width: 100%;
      height: 100%;
    }

    .preview-placeholder {
      padding: 12px;
      color: var(--affine-text-secondary-color);
      font-size: var(--affine-font-xs);
      text-align: center;
    }

    .status {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      min-height: 28px;
      padding: 6px 8px;
      border-top: 1px solid var(--affine-border-color);
      background: var(--affine-background-overlay-panel-color);
      color: var(--affine-text-secondary-color);
      font-size: var(--affine-font-xs);
      line-height: 16px;
    }

    .status.invalid,
    .status.error {
      color: var(--affine-error-color);
    }

    .technical {
      max-width: 65%;
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font: inherit;
      opacity: 0.8;
    }
  `;

  @query('textarea.source-editor')
  private accessor _textarea: HTMLTextAreaElement | null = null;

  @query('textarea.inline-node-editor')
  private accessor _inlineTextarea: HTMLTextAreaElement | null = null;

  @state()
  private accessor _source = '';

  @state()
  private accessor _inlineDraft = '';

  @state()
  private accessor _connectPointer: { x: number; y: number } | null = null;

  @state()
  private accessor _connectTargetId: string | null = null;

  private _activeModel: MermaidElementModel | null = null;
  private _familyController: MermaidFamilyEditingController | null = null;
  private _flowchartController: MermaidFlowchartEditingController | null = null;
  private _geometryModule: {
    hitTestMermaidInteractionSnapshot: typeof hitTestMermaidInteractionSnapshot;
    mermaidSvgPointToElementLocal: typeof mermaidSvgPointToElementLocal;
  } | null = null;
  private _flowchartShapes: readonly ShapeId[] = [];
  private _visualRuntimePromise: Promise<void> | null = null;
  private _inlineEntityId: string | null = null;
  private _inlineOriginal = '';
  private _inlineComposing = false;
  private _inlineCommitTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly _transientStateDisposers: Array<() => void> = [];
  private _sourceObserver:
    | ((event: { delta: MermaidTextDeltaOperation[] }) => void)
    | null = null;

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  override firstUpdated() {
    this._syncActiveModel();

    this._disposables.add(
      this.gfx.selection.slots.updated.subscribe(() => this._syncActiveModel())
    );
    this._disposables.add(
      this.gfx.viewport.viewportUpdated.subscribe(() => this.requestUpdate())
    );
    this._disposables.add(
      this.std.event.add('keyDown', ctx =>
        this._handleVisualGlobalKey(ctx.get('keyboardState').raw)
      )
    );
    this._disposables.add(
      this.store.readonly$.subscribe(() => this.requestUpdate())
    );
    if (this.gfx.surface) {
      this._disposables.add(
        this.gfx.surface.elementUpdated.subscribe(() => this.requestUpdate())
      );
      this._disposables.add(
        this.gfx.surface.elementRemoved.subscribe(() => this._syncActiveModel())
      );
    }
  }

  override disconnectedCallback() {
    this._clearInlineCommitTimer();
    this._disposeVisualRuntime();
    this._unbindSource();
    super.disconnectedCallback();
  }

  private _clearInlineCommitTimer() {
    if (this._inlineCommitTimer) {
      clearTimeout(this._inlineCommitTimer);
      this._inlineCommitTimer = null;
    }
  }

  private _disposeTransientStateSubscriptions() {
    for (const dispose of this._transientStateDisposers.splice(0)) {
      dispose();
    }
  }

  private _disposeVisualRuntime() {
    this._familyController?.dispose();
    this._familyController = null;
    this._flowchartController?.dispose();
    this._flowchartController = null;
    this._geometryModule = null;
    this._visualRuntimePromise = null;
    this._inlineEntityId = null;
    this._inlineDraft = '';
    this._inlineOriginal = '';
    this._flowchartShapes = [];
    this._connectPointer = null;
    this._connectTargetId = null;
    this._disposeTransientStateSubscriptions();
  }

  private _ensureVisualRuntime(model: MermaidElementModel) {
    if (
      this._flowchartController?.model === model &&
      this._familyController?.model === model &&
      this._geometryModule
    ) {
      return Promise.resolve();
    }
    if (this._visualRuntimePromise) return this._visualRuntimePromise;

    this._visualRuntimePromise = Promise.all([
      import('./flowchart-editing.js'),
      import('./family-editing.js'),
      import('../renderer/interaction-geometry.js'),
    ])
      .then(([flowchart, family, geometry]) => {
        this._flowchartController?.dispose();
        this._familyController?.dispose();
        this._flowchartController =
          new flowchart.MermaidFlowchartEditingController(model);
        this._familyController = new family.MermaidFamilyEditingController(
          model
        );
        this._flowchartShapes = flowchart.FLOWCHART_MVP_SHAPES;
        this._geometryModule = geometry;
        this._syncInlineDraft(model);
        this.requestUpdate();
      })
      .finally(() => {
        this._visualRuntimePromise = null;
      });

    return this._visualRuntimePromise;
  }

  private _inlineEntityLabel(model: MermaidElementModel, entityId: string) {
    if (entityId.startsWith('node:')) {
      if (this._flowchartController?.model !== model) return null;
      return (
        this._flowchartController.flowchart?.nodeById.get(entityId.slice(5))
          ?.label ?? null
      );
    }
    if (this._familyController?.model !== model) return null;
    return this._familyController.describe(entityId)?.label ?? null;
  }

  private _syncInlineDraft(model: MermaidElementModel) {
    const entityId = getMermaidVisualEditingSignal(model).value.inlineEntityId;
    if (!entityId || !isMermaidInlineEditableEntityId(entityId)) {
      this._clearInlineCommitTimer();
      this._inlineEntityId = null;
      this._inlineDraft = '';
      this._inlineOriginal = '';
      return;
    }
    const label = this._inlineEntityLabel(model, entityId);
    if (label === null) return;
    if (entityId !== this._inlineEntityId) {
      this._clearInlineCommitTimer();
      this._inlineEntityId = entityId;
      this._inlineOriginal = label.replaceAll('\\n', '\n');
      this._inlineDraft = this._inlineOriginal;
      this.updateComplete
        .then(() => {
          const textarea = this._inlineTextarea;
          if (!textarea) return;
          textarea.focus();
          textarea.select();
        })
        .catch(console.error);
    }
  }

  private _scheduleInlineCommit() {
    this._clearInlineCommitTimer();
    this._inlineCommitTimer = setTimeout(() => {
      this._inlineCommitTimer = null;
      this._commitInlineDraft();
    }, INLINE_COMMIT_DEBOUNCE_MS);
  }

  private _commitInlineDraft() {
    const model = this._activeModel;
    const entityId = this._inlineEntityId;
    if (
      !model ||
      !entityId ||
      !this._isEditable(model) ||
      this._inlineComposing
    ) {
      return;
    }
    const next = this._inlineDraft.replaceAll('\r\n', '\n');
    if (next === this._inlineOriginal) return;
    const mermaidLabel = next.replaceAll('\n', '\\n');
    const result = entityId.startsWith('node:')
      ? this._flowchartController?.renameNode(entityId, mermaidLabel)
      : this._familyController?.setLabel(entityId, mermaidLabel);
    if (result?.status === 'applied' || result?.status === 'noop') {
      this._inlineOriginal = next;
      this._source = model.source.toString();
    }
  }

  private _cancelInlineEditing(model: MermaidElementModel) {
    this._clearInlineCommitTimer();
    this._inlineDraft = this._inlineOriginal;
    setMermaidInlineEditing(model, null);
    this._inlineEntityId = null;
    this.requestUpdate();
  }

  private readonly _onInlineInput = (event: InputEvent) => {
    const textarea = event.currentTarget as HTMLTextAreaElement;
    this._inlineDraft = textarea.value;
    if (!event.isComposing) this._scheduleInlineCommit();
  };

  private readonly _onInlineCompositionStart = () => {
    this._inlineComposing = true;
    this._clearInlineCommitTimer();
  };

  private readonly _onInlineCompositionEnd = (event: CompositionEvent) => {
    this._inlineComposing = false;
    this._inlineDraft = (event.currentTarget as HTMLTextAreaElement).value;
    this._scheduleInlineCommit();
  };

  private readonly _onInlineBlur = () => {
    this._clearInlineCommitTimer();
    this._commitInlineDraft();
  };

  private readonly _onInlineKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    const model = this._activeModel;
    if (!model) return;
    if (event.isComposing) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this._cancelInlineEditing(model);
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this._clearInlineCommitTimer();
      this._commitInlineDraft();
      setMermaidInlineEditing(model, null);
      this._inlineEntityId = null;
      this.requestUpdate();
    }
  };

  private _nodeEntityStyle(
    model: MermaidElementModel,
    snapshot: MermaidInteractionSnapshot,
    entityId: string | null
  ) {
    if (!entityId) return null;
    const entity =
      snapshot.entities.find(item => item.entityId === entityId) ?? null;
    return entity ? this._visualEntityStyle(model, snapshot, entity) : null;
  }

  private _runNodeAction(
    model: MermaidElementModel,
    action: (controller: MermaidFlowchartEditingController) => void
  ) {
    const controller = this._flowchartController;
    if (!controller || controller.model !== model || !this._isEditable(model)) {
      return;
    }
    action(controller);
    this._source = model.source.toString();
    this.requestUpdate();
  }

  private _setNodeShape(
    model: MermaidElementModel,
    entityId: string,
    shape: ShapeId
  ) {
    this._runNodeAction(model, controller => {
      controller.setNodeShape(entityId, shape);
    });
  }

  private _duplicateNode(model: MermaidElementModel, entityId: string) {
    this._runNodeAction(model, controller => {
      controller.duplicateNode(entityId);
    });
  }

  private _deleteNode(model: MermaidElementModel, entityId: string) {
    this._runNodeAction(model, controller => {
      controller.deleteNode(entityId);
      controller.select(null);
      setMermaidInlineEditing(model, null);
      setMermaidAddPicker(model, null);
      setMermaidConnectSource(model, null);
    });
  }

  private _setEdgeLabel(
    model: MermaidElementModel,
    entityId: string,
    label: string
  ) {
    this._runNodeAction(model, controller => {
      controller.setEdgeLabel(entityId, label.trim());
    });
  }

  private _deleteEdge(model: MermaidElementModel, entityId: string) {
    this._runNodeAction(model, controller => {
      controller.deleteEdge(entityId);
      controller.select(null);
    });
  }

  private _modifyWithAI(model: MermaidElementModel) {
    const provider = this.std.getOptional(MermaidActionProvider);
    const input = { elementId: model.id, source: model.source.toString() };
    if (!provider?.can('modify-ai', input)) return;
    Promise.resolve(provider.run('modify-ai', input)).catch(console.error);
  }

  private _toggleAddPicker(model: MermaidElementModel, entityId: string) {
    const current =
      getMermaidVisualEditingSignal(model).value.addPickerEntityId;
    setMermaidAddPicker(model, current === entityId ? null : entityId);
    setMermaidInlineEditing(model, null);
  }

  private _addAdjacentNode(
    model: MermaidElementModel,
    entityId: string,
    shape: ShapeId
  ) {
    this._runNodeAction(model, controller => {
      const result = controller.addAdjacentNode(entityId, {
        nodeLabel: tr(this, 'New node'),
        nodeShape: shape,
      });
      if (result.status === 'applied' && result.created[0]) {
        setMermaidVirtualSelection(model, result.created[0]);
        setMermaidInlineEditing(model, result.created[0]);
      }
      setMermaidAddPicker(model, null);
    });
  }

  private _isValidConnectTarget(
    sourceEntityId: string,
    targetEntityId: string | null
  ) {
    if (!targetEntityId || sourceEntityId === targetEntityId) {
      return false;
    }

    if (
      sourceEntityId.startsWith('node:') &&
      targetEntityId.startsWith('node:')
    ) {
      const graph = this._flowchartController?.flowchart;
      if (!graph) return false;
      const source = sourceEntityId.slice(5);
      const target = targetEntityId.slice(5);
      return !graph.edges.some(
        edge => edge.source === source && edge.target === target
      );
    }

    return (
      this._familyController?.canConnect(sourceEntityId, targetEntityId) ??
      false
    );
  }

  private _beginConnect(
    event: PointerEvent,
    model: MermaidElementModel,
    entityId: string
  ) {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    setMermaidInlineEditing(model, null);
    setMermaidAddPicker(model, null);
    setMermaidConnectSource(model, entityId);
    this._connectTargetId = null;
    this._connectPointer = { x: event.clientX, y: event.clientY };
  }

  private _moveConnect(event: PointerEvent, model: MermaidElementModel) {
    const sourceEntityId =
      getMermaidVisualEditingSignal(model).value.connectSourceEntityId;
    const snapshot = getMermaidRenderStateSignal(model).value.interaction;
    const geometry = this._geometryModule;
    if (!sourceEntityId || !snapshot || !geometry) return;

    event.preventDefault();
    event.stopPropagation();
    const [modelX, modelY] = this.gfx.viewport.toModelCoord(
      event.clientX,
      event.clientY
    );
    const hit = geometry.hitTestMermaidInteractionSnapshot(snapshot, model, [
      modelX,
      modelY,
    ]);
    this._connectTargetId =
      hit && this._isValidConnectTarget(sourceEntityId, hit.entityId)
        ? hit.entityId
        : null;
    this._connectPointer = { x: event.clientX, y: event.clientY };
  }

  private _endConnect(event: PointerEvent, model: MermaidElementModel) {
    const sourceEntityId =
      getMermaidVisualEditingSignal(model).value.connectSourceEntityId;
    const targetEntityId = this._connectTargetId;
    event.preventDefault();
    event.stopPropagation();

    if (
      sourceEntityId &&
      targetEntityId &&
      this._isValidConnectTarget(sourceEntityId, targetEntityId)
    ) {
      if (sourceEntityId.startsWith('node:')) {
        this._runNodeAction(model, controller => {
          controller.connect(sourceEntityId, targetEntityId);
        });
      } else {
        this._runFamilyAction(model, controller => {
          controller.connect(sourceEntityId, targetEntityId);
        });
      }
    }

    setMermaidConnectSource(model, null);
    this._connectTargetId = null;
    this._connectPointer = null;
  }

  private _visualEntityStyle(
    model: MermaidElementModel,
    snapshot: MermaidInteractionSnapshot,
    entity: MermaidInteractionEntityGeometry
  ) {
    const geometry = this._geometryModule;
    if (!geometry) return null;
    const [, , modelWidth, modelHeight] = model.deserializedXYWH;
    const topLeft = geometry.mermaidSvgPointToElementLocal(
      snapshot,
      { x: entity.bounds.x, y: entity.bounds.y },
      modelWidth,
      modelHeight
    );
    const bottomRight = geometry.mermaidSvgPointToElementLocal(
      snapshot,
      {
        x: entity.bounds.x + entity.bounds.width,
        y: entity.bounds.y + entity.bounds.height,
      },
      modelWidth,
      modelHeight
    );
    if (!topLeft || !bottomRight) return null;
    const [viewX, viewY] = this.gfx.viewport.toViewCoord(
      model.x + topLeft.x,
      model.y + topLeft.y
    );
    const [viewRight, viewBottom] = this.gfx.viewport.toViewCoord(
      model.x + bottomRight.x,
      model.y + bottomRight.y
    );
    return {
      left: String(Math.min(viewX, viewRight)) + 'px',
      top: String(Math.min(viewY, viewBottom)) + 'px',
      width: String(Math.max(24, Math.abs(viewRight - viewX))) + 'px',
      height: String(Math.max(24, Math.abs(viewBottom - viewY))) + 'px',
    };
  }

  private _visualHitAreaStyle(model: MermaidElementModel) {
    const [x, y, width, height] = model.deserializedXYWH;
    const [viewX, viewY] = this.gfx.viewport.toViewCoord(x, y);
    const [viewRight, viewBottom] = this.gfx.viewport.toViewCoord(
      x + width,
      y + height
    );
    return {
      left: String(Math.min(viewX, viewRight)) + 'px',
      top: String(Math.min(viewY, viewBottom)) + 'px',
      width: String(Math.abs(viewRight - viewX)) + 'px',
      height: String(Math.abs(viewBottom - viewY)) + 'px',
    };
  }

  private _selectVisualEntityAtPointer(
    event: MouseEvent,
    model: MermaidElementModel,
    inline: boolean
  ) {
    if (!this._isEditable(model)) return;
    const snapshot = getMermaidRenderStateSignal(model).value.interaction;
    const geometry = this._geometryModule;
    if (!snapshot || !geometry) return;

    event.preventDefault();
    event.stopPropagation();
    const [modelX, modelY] = this.gfx.viewport.toModelCoordFromClientCoord([
      event.clientX,
      event.clientY,
    ]);
    const hit = geometry.hitTestMermaidInteractionSnapshot(snapshot, model, [
      modelX,
      modelY,
    ]);
    const entityId = hit?.entityId ?? null;
    setMermaidVirtualSelection(model, entityId);
    setMermaidInlineEditing(model, inline ? entityId : null);
    setMermaidAddPicker(model, null);
    setMermaidConnectSource(model, null);
    this.requestUpdate();
  }

  private readonly _onVisualHitClick = (event: MouseEvent) => {
    const model = this._activeModel;
    if (!model) return;
    this._selectVisualEntityAtPointer(event, model, false);
  };

  private readonly _onVisualHitDoubleClick = (event: MouseEvent) => {
    const model = this._activeModel;
    if (!model) return;
    this._selectVisualEntityAtPointer(event, model, true);
  };

  private _isEditable(model: MermaidElementModel) {
    return (
      !this.store.readonly && !this.gfx.viewport.locked && !model.isLocked()
    );
  }

  private _syncActiveModel() {
    const selected = this.gfx.selection.selectedElements;
    const model =
      this.gfx.selection.editing &&
      selected.length === 1 &&
      selected[0] instanceof MermaidElementModel
        ? selected[0]
        : null;

    if (model === this._activeModel) {
      this.requestUpdate();
      return;
    }

    if (this._activeModel) {
      this.store.captureSync();
    }
    this._disposeVisualRuntime();
    this._unbindSource();
    this._activeModel = model;
    if (model) {
      this.store.captureSync();
      this._source = model.source.toString();
      this._sourceObserver = event => this._onRemoteSource(event.delta);
      model.source.observe(this._sourceObserver);
      this._transientStateDisposers.push(
        getMermaidViewModeSignal(model).subscribe(() => this.requestUpdate()),
        getMermaidVirtualSelectionSignal(model).subscribe(() =>
          this.requestUpdate()
        ),
        getMermaidRenderStateSignal(model).subscribe(() => {
          this._syncInlineDraft(model);
          this.requestUpdate();
        }),
        getMermaidVisualEditingSignal(model).subscribe(() => {
          this._ensureVisualRuntime(model)
            .then(() => {
              this._syncInlineDraft(model);
              this.requestUpdate();
            })
            .catch(console.error);
        })
      );
      if (getMermaidViewModeSignal(model).value === 'diagram') {
        this._ensureVisualRuntime(model).catch(console.error);
      }
    }
    this.requestUpdate();

    if (model && this._isEditable(model)) {
      this.updateComplete
        .then(() => this._textarea?.focus())
        .catch(console.error);
    }
  }

  private _unbindSource() {
    if (this._activeModel && this._sourceObserver) {
      this._activeModel.source.unobserve(this._sourceObserver);
    }
    this._sourceObserver = null;
  }

  private _onRemoteSource(delta: MermaidTextDeltaOperation[]) {
    const model = this._activeModel;
    if (!model) return;

    const next = model.source.toString();
    if (next === this._source) return;

    const textarea = this._textarea;
    const start = textarea?.selectionStart ?? null;
    const end = textarea?.selectionEnd ?? null;
    this._source = next;
    this._flowchartController?.syncSelection();
    this._familyController?.syncSelection();
    if (
      this._activeModel &&
      getMermaidViewModeSignal(this._activeModel).value === 'diagram' &&
      !this._inlineComposing &&
      this._inlineDraft === this._inlineOriginal
    ) {
      this._syncInlineDraft(this._activeModel);
    }
    this.requestUpdate();

    if (start !== null && end !== null) {
      const nextStart = transformMermaidTextIndex(start, delta);
      const nextEnd = transformMermaidTextIndex(end, delta);
      this.updateComplete
        .then(() => {
          this._textarea?.setSelectionRange(nextStart, nextEnd);
        })
        .catch(console.error);
    }
  }

  private _commitTextareaValue(textarea: HTMLTextAreaElement) {
    const model = this._activeModel;
    if (!model || !this._isEditable(model)) return;

    const next = textarea.value.replaceAll('\r\n', '\n');
    this._source = next;
    applyMermaidSourceInput(model, next);
  }

  private readonly _onInput = (event: InputEvent) => {
    const textarea = event.currentTarget as HTMLTextAreaElement;
    this._source = textarea.value;
    if (!event.isComposing) {
      this._commitTextareaValue(textarea);
    }
  };

  private readonly _onCompositionEnd = (event: CompositionEvent) => {
    this._commitTextareaValue(event.currentTarget as HTMLTextAreaElement);
  };

  private _canClose(model: MermaidElementModel) {
    const status = getMermaidRenderStateSignal(model).value.status;
    return status === 'ready' || status === 'degraded';
  }

  private readonly _onKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    const model = this._activeModel;
    if (!model) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      if (this._canClose(model)) {
        showMermaidDiagram(this.gfx, model);
      } else {
        this._textarea?.focus();
      }
      return;
    }

    if (
      event.key === 'Enter' &&
      (event.ctrlKey || event.metaKey) &&
      this._canClose(model)
    ) {
      event.preventDefault();
      showMermaidDiagram(this.gfx, model);
    }
  };

  private _handleVisualGlobalKey(event: KeyboardEvent) {
    const model = this._activeModel;
    if (
      !model ||
      getMermaidViewModeSignal(model).value !== 'diagram' ||
      !this.gfx.selection.editing
    ) {
      return false;
    }

    const target = event.target;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      event.isComposing
    ) {
      return false;
    }

    const selectedId = getMermaidVirtualSelectionSignal(model).value;
    const visual = getMermaidVisualEditingSignal(model).value;

    if (event.key === 'Escape') {
      if (visual.inlineEntityId) {
        this._cancelInlineEditing(model);
      } else if (visual.addPickerEntityId || visual.connectSourceEntityId) {
        setMermaidAddPicker(model, null);
        setMermaidConnectSource(model, null);
        this._connectPointer = null;
        this._connectTargetId = null;
      } else {
        showMermaidDiagram(this.gfx, model);
      }
      return true;
    }

    if (
      (event.key === 'Enter' || event.key === 'F2') &&
      isMermaidInlineEditableEntityId(selectedId)
    ) {
      setMermaidInlineEditing(model, selectedId);
      return true;
    }

    if ((event.key === 'Delete' || event.key === 'Backspace') && selectedId) {
      if (selectedId.startsWith('node:')) {
        this._deleteNode(model, selectedId);
        return true;
      }
      if (selectedId.startsWith('edge:')) {
        this._deleteEdge(model, selectedId);
        return true;
      }
      if (this._familyController?.describe(selectedId)) {
        this._deleteFamilyEntity(model, selectedId);
        return true;
      }
    }

    if (event.key === 'Tab' && this._flowchartController?.flowchart) {
      const nodes = this._flowchartController.flowchart.nodes;
      if (!nodes.length) return false;
      const current = selectedId?.startsWith('node:')
        ? nodes.findIndex(node => node.entityId === selectedId)
        : -1;
      const delta = event.shiftKey ? -1 : 1;
      const nextIndex =
        current < 0
          ? event.shiftKey
            ? nodes.length - 1
            : 0
          : (current + delta + nodes.length) % nodes.length;
      this._flowchartController.select(nodes[nextIndex].entityId);
      setMermaidInlineEditing(model, null);
      return true;
    }

    if (event.key === 'Tab' && this._familyController?.capability?.editable) {
      const interaction = getMermaidRenderStateSignal(model).value.interaction;
      const entities =
        interaction?.entities.filter(entity => {
          const descriptor = this._familyController?.describe(entity.entityId);
          return descriptor && descriptor.kind !== 'connection';
        }) ?? [];
      if (!entities.length) return false;
      const current = selectedId
        ? entities.findIndex(entity => entity.entityId === selectedId)
        : -1;
      const delta = event.shiftKey ? -1 : 1;
      const nextIndex =
        current < 0
          ? event.shiftKey
            ? entities.length - 1
            : 0
          : (current + delta + entities.length) % entities.length;
      this._familyController.select(entities[nextIndex].entityId);
      setMermaidInlineEditing(model, null);
      return true;
    }

    return false;
  }

  private readonly _onVisualKeyDown = (event: KeyboardEvent) => {
    const model = this._activeModel;
    if (!model) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (getMermaidVisualEditingSignal(model).value.inlineEntityId) {
        this._cancelInlineEditing(model);
      } else {
        showMermaidDiagram(this.gfx, model);
      }
    }
  };

  private _runFamilyAction(
    model: MermaidElementModel,
    action: (controller: MermaidFamilyEditingController) => void
  ) {
    const controller = this._familyController;
    if (!controller || controller.model !== model || !this._isEditable(model)) {
      return;
    }
    action(controller);
    this._source = model.source.toString();
    this.requestUpdate();
  }

  private _setFamilyLabel(
    model: MermaidElementModel,
    entityId: string,
    label: string
  ) {
    this._runFamilyAction(model, controller => {
      controller.setLabel(entityId, label.trim());
    });
  }

  private _setFamilyType(
    model: MermaidElementModel,
    entityId: string,
    value: string
  ) {
    this._runFamilyAction(model, controller => {
      controller.setType(entityId, value);
    });
  }

  private _setFamilySecondary(
    model: MermaidElementModel,
    entityId: string,
    value: string
  ) {
    this._runFamilyAction(model, controller => {
      controller.setSecondary(entityId, value);
    });
  }

  private _addFamilyDetail(model: MermaidElementModel, entityId: string) {
    this._runFamilyAction(model, controller => {
      controller.addDetail(entityId);
    });
  }

  private _addFamilyDefault(model: MermaidElementModel) {
    this._runFamilyAction(model, controller => {
      const result = controller.addDefault();
      const created = result?.created[0] ?? null;
      if (result?.status === 'applied' && created) {
        setMermaidVirtualSelection(model, created);
        if (isMermaidInlineEditableEntityId(created)) {
          setMermaidInlineEditing(model, created);
        }
      }
    });
  }

  private _deleteFamilyEntity(model: MermaidElementModel, entityId: string) {
    this._runFamilyAction(model, controller => {
      const result = controller.deleteEntity(entityId);
      if (result?.status === 'applied') {
        controller.select(null);
        setMermaidInlineEditing(model, null);
      }
    });
  }

  private _renderFamilyToolbar(
    model: MermaidElementModel,
    entity: MermaidInteractionEntityGeometry,
    entityStyle: Record<string, string>
  ) {
    const controller = this._familyController;
    if (!controller || controller.model !== model) return nothing;
    const descriptor = controller.describe(entity.entityId);
    if (!descriptor) return nothing;
    const toolbarStyle = this._clampedOverlayStyle(
      entityStyle,
      { width: 520, height: 40 },
      'above'
    );
    const secondaryEditable =
      controller.diagramType === 'pie' || controller.diagramType === 'gantt';

    return html`
      <div
        class="visual-toolbar family-toolbar"
        style=${styleMap(toolbarStyle)}
        role="toolbar"
        aria-label=${tr(this, 'Mermaid entity actions')}
        @pointerdown=${stopEvent}
        @click=${stopEvent}
      >
        <input
          type="text"
          .value=${descriptor.label}
          aria-label=${tr(this, 'Entity label')}
          title=${tr(this, 'Entity label')}
          @change=${(event: Event) =>
            this._setFamilyLabel(
              model,
              entity.entityId,
              (event.currentTarget as HTMLInputElement).value
            )}
          @keydown=${(event: KeyboardEvent) => {
            event.stopPropagation();
            if (event.isComposing) return;
            if (event.key === 'Enter') {
              event.preventDefault();
              (event.currentTarget as HTMLInputElement).blur();
            }
          }}
        />
        ${
          descriptor.typeOptions.length && descriptor.typeValue
            ? html`
                <select
                  aria-label=${tr(this, 'Entity type')}
                  @change=${(event: Event) =>
                    this._setFamilyType(
                      model,
                      entity.entityId,
                      (event.currentTarget as HTMLSelectElement).value
                    )}
                >
                  ${descriptor.typeOptions.map(
                    option => html`
                      <option
                        value=${option}
                        ?selected=${option === descriptor.typeValue}
                      >
                        ${option}
                      </option>
                    `
                  )}
                </select>
              `
            : nothing
        }
        ${
          secondaryEditable
            ? html`
                <input
                  type="text"
                  .value=${descriptor.secondary ?? ''}
                  aria-label=${tr(
                    this,
                    controller.diagramType === 'pie' ? 'Value' : 'Task metadata'
                  )}
                  title=${tr(
                    this,
                    controller.diagramType === 'pie' ? 'Value' : 'Task metadata'
                  )}
                  @change=${(event: Event) =>
                    this._setFamilySecondary(
                      model,
                      entity.entityId,
                      (event.currentTarget as HTMLInputElement).value
                    )}
                  @keydown=${(event: KeyboardEvent) => {
                    event.stopPropagation();
                    if (event.isComposing) return;
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      (event.currentTarget as HTMLInputElement).blur();
                    }
                  }}
                />
              `
            : nothing
        }
        ${
          descriptor.addDetail
            ? html`
                <button
                  type="button"
                  title=${tr(
                    this,
                    descriptor.addDetail === 'member'
                      ? 'Add member'
                      : 'Add attribute'
                  )}
                  @click=${() => this._addFamilyDetail(model, entity.entityId)}
                >
                  ${tr(
                    this,
                    descriptor.addDetail === 'member'
                      ? 'Add member'
                      : 'Add attribute'
                  )}
                </button>
              `
            : nothing
        }
        <button
          type="button"
          title=${tr(this, 'Add item')}
          @click=${() => this._addFamilyDefault(model)}
        >
          ${tr(this, 'Add')}
        </button>
        ${
          descriptor.deletable
            ? html`
                <button
                  type="button"
                  title=${tr(this, 'Delete')}
                  @click=${() =>
                    this._deleteFamilyEntity(model, entity.entityId)}
                >
                  ${tr(this, 'Delete')}
                </button>
              `
            : nothing
        }
      </div>
    `;
  }

  private _shapeLabel(shape: ShapeId) {
    const labels: Partial<Record<ShapeId, string>> = {
      rect: 'Process',
      round: 'Rounded',
      stadium: 'Stadium',
      subroutine: 'Subroutine',
      cylinder: 'Database',
      circle: 'Circle',
      doublecircle: 'Double circle',
      diamond: 'Decision',
      hexagon: 'Hexagon',
      lean_r: 'Lean right',
      lean_l: 'Lean left',
      trap_t: 'Trapezoid top',
      trap_b: 'Trapezoid bottom',
      odd: 'Asymmetric',
    };
    return tr(this, labels[shape] ?? 'Process');
  }

  private _clampedOverlayStyle(
    entityStyle: Record<string, string>,
    overlay: { height: number; width: number },
    placement: 'above' | 'below'
  ) {
    const anchor = {
      left: Number.parseFloat(entityStyle.left),
      top: Number.parseFloat(entityStyle.top),
      width: Number.parseFloat(entityStyle.width),
      height: Number.parseFloat(entityStyle.height),
    };
    const viewport = {
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight,
    };
    const position = clampMermaidOverlayPosition(
      anchor,
      overlay,
      viewport,
      placement
    );
    return {
      left: String(position.left) + 'px',
      top: String(position.top) + 'px',
    };
  }

  private _renderNodeToolbar(
    model: MermaidElementModel,
    entity: MermaidInteractionEntityGeometry,
    entityStyle: Record<string, string>
  ) {
    const node = this._flowchartController?.flowchart?.nodeById.get(
      entity.entityId.slice(5)
    );
    if (!node) return nothing;
    const input = { elementId: model.id, source: model.source.toString() };
    const provider = this.std.getOptional(MermaidActionProvider);
    const canModifyAI = provider?.can('modify-ai', input) ?? false;
    const toolbarStyle = this._clampedOverlayStyle(
      entityStyle,
      { width: 360, height: 40 },
      'above'
    );

    return html`
      <div
        class="visual-toolbar"
        style=${styleMap(toolbarStyle)}
        role="toolbar"
        aria-label=${tr(this, 'Mermaid node actions')}
        @pointerdown=${stopEvent}
        @click=${stopEvent}
      >
        <select
          aria-label=${tr(this, 'Node shape')}
          .value=${node.shape}
          @change=${(event: Event) =>
            this._setNodeShape(
              model,
              entity.entityId,
              (event.currentTarget as HTMLSelectElement).value as ShapeId
            )}
        >
          ${this._flowchartShapes.map(
            shape =>
              html`<option value=${shape}>${this._shapeLabel(shape)}</option>`
          )}
        </select>
        <button
          type="button"
          title=${tr(this, 'Duplicate')}
          @click=${() => this._duplicateNode(model, entity.entityId)}
        >
          ${tr(this, 'Duplicate')}
        </button>
        ${
          canModifyAI
            ? html`<button
                type="button"
                title=${tr(this, 'Modify with AI')}
                @click=${() => this._modifyWithAI(model)}
              >
                ${tr(this, 'AI')}
              </button>`
            : nothing
        }
        <button
          type="button"
          title=${tr(this, 'Delete')}
          @click=${() => this._deleteNode(model, entity.entityId)}
        >
          ${tr(this, 'Delete')}
        </button>
      </div>
    `;
  }

  private _renderNodeAffordances(
    model: MermaidElementModel,
    entity: MermaidInteractionEntityGeometry,
    entityStyle: Record<string, string>
  ) {
    const visual = getMermaidVisualEditingSignal(model).value;
    const addStyle = {
      left: `calc(${entityStyle.left} + ${entityStyle.width} / 2)`,
      top: `calc(${entityStyle.top} + ${entityStyle.height})`,
    };
    const connectStyle = {
      left: `calc(${entityStyle.left} + ${entityStyle.width})`,
      top: `calc(${entityStyle.top} + ${entityStyle.height} / 2)`,
    };
    const pickerStyle = this._clampedOverlayStyle(
      entityStyle,
      { width: 300, height: 40 },
      'below'
    );
    const quickShapes: Array<{ label: string; shape: ShapeId }> = [
      { label: tr(this, 'Process'), shape: 'rect' },
      { label: tr(this, 'Rounded'), shape: 'round' },
      { label: tr(this, 'Circle'), shape: 'circle' },
      { label: tr(this, 'Decision'), shape: 'diamond' },
    ];

    return html`
      <button
        class="add-node-handle"
        type="button"
        style=${styleMap(addStyle)}
        title=${tr(this, 'Add connected node')}
        aria-label=${tr(this, 'Add connected node')}
        @pointerdown=${stopEvent}
        @click=${() => this._toggleAddPicker(model, entity.entityId)}
      >
        +
      </button>
      <button
        class="connect-handle"
        type="button"
        style=${styleMap(connectStyle)}
        title=${tr(this, 'Connect node')}
        aria-label=${tr(this, 'Connect node')}
        @pointerdown=${(event: PointerEvent) =>
          this._beginConnect(event, model, entity.entityId)}
        @pointermove=${(event: PointerEvent) => this._moveConnect(event, model)}
        @pointerup=${(event: PointerEvent) => this._endConnect(event, model)}
        @pointercancel=${(event: PointerEvent) => this._endConnect(event, model)}
      ></button>
      ${
        visual.addPickerEntityId === entity.entityId
          ? html`
              <div
                class="add-picker"
                style=${styleMap(pickerStyle)}
                role="menu"
                aria-label=${tr(this, 'Choose node shape')}
                @pointerdown=${stopEvent}
                @click=${stopEvent}
              >
                ${quickShapes.map(
                  option => html`<button
                    type="button"
                    role="menuitem"
                    title=${option.label}
                    @click=${() =>
                      this._addAdjacentNode(
                        model,
                        entity.entityId,
                        option.shape
                      )}
                  >
                    ${option.label}
                  </button>`
                )}
              </div>
            `
          : nothing
      }
    `;
  }

  private _renderFamilyConnectAffordance(
    model: MermaidElementModel,
    entity: MermaidInteractionEntityGeometry,
    entityStyle: Record<string, string>
  ) {
    const controller = this._familyController;
    const descriptor = controller?.describe(entity.entityId);
    if (
      !controller ||
      controller.model !== model ||
      !controller.capability?.actions.includes('connect') ||
      !descriptor ||
      (descriptor.kind !== 'entity' && descriptor.kind !== 'subgraph')
    ) {
      return nothing;
    }
    const connectStyle = {
      left: `calc(${entityStyle.left} + ${entityStyle.width})`,
      top: `calc(${entityStyle.top} + ${entityStyle.height} / 2)`,
    };

    return html`
      <button
        class="connect-handle family-connect-handle"
        type="button"
        style=${styleMap(connectStyle)}
        title=${tr(this, 'Connect entity')}
        aria-label=${tr(this, 'Connect entity')}
        @pointerdown=${(event: PointerEvent) =>
          this._beginConnect(event, model, entity.entityId)}
        @pointermove=${(event: PointerEvent) => this._moveConnect(event, model)}
        @pointerup=${(event: PointerEvent) => this._endConnect(event, model)}
        @pointercancel=${(event: PointerEvent) => this._endConnect(event, model)}
      ></button>
    `;
  }

  private _renderConnectFeedback(
    model: MermaidElementModel,
    snapshot: MermaidInteractionSnapshot,
    selectedEntity: MermaidInteractionEntityGeometry | null,
    selectedStyle: Record<string, string> | null
  ) {
    const sourceEntityId =
      getMermaidVisualEditingSignal(model).value.connectSourceEntityId;
    if (!sourceEntityId || !this._connectPointer) return nothing;
    const sourceStyle =
      sourceEntityId === selectedEntity?.entityId
        ? selectedStyle
        : this._nodeEntityStyle(model, snapshot, sourceEntityId);
    const targetStyle = this._nodeEntityStyle(
      model,
      snapshot,
      this._connectTargetId
    );
    if (!sourceStyle) return nothing;
    const x1 = parseFloat(sourceStyle.left) + parseFloat(sourceStyle.width);
    const y1 = parseFloat(sourceStyle.top) + parseFloat(sourceStyle.height) / 2;

    return html`
      <svg class="connect-preview" aria-hidden="true">
        <line
          x1=${x1}
          y1=${y1}
          x2=${this._connectPointer.x}
          y2=${this._connectPointer.y}
        ></line>
      </svg>
      ${
        targetStyle
          ? html`<div
              class="connect-target"
              style=${styleMap(targetStyle)}
              aria-hidden="true"
            ></div>`
          : nothing
      }
    `;
  }

  private _renderEdgeToolbar(
    model: MermaidElementModel,
    snapshot: MermaidInteractionSnapshot,
    entity: MermaidInteractionEntityGeometry
  ) {
    const edge = this._flowchartController?.flowchart?.edges.find(
      item => item.entityId === entity.entityId
    );
    if (!edge) return nothing;
    const labelEntity = entity.labelBounds
      ? { ...entity, bounds: entity.labelBounds }
      : entity;
    const entityStyle = this._visualEntityStyle(model, snapshot, labelEntity);
    if (!entityStyle) return nothing;

    const toolbarStyle = this._clampedOverlayStyle(
      entityStyle,
      { width: 320, height: 40 },
      'above'
    );

    return html`
      <div
        class="visual-toolbar edge-toolbar"
        style=${styleMap(toolbarStyle)}
        role="toolbar"
        aria-label=${tr(this, 'Mermaid edge actions')}
        @pointerdown=${stopEvent}
        @click=${stopEvent}
      >
        <input
          type="text"
          .value=${edge.label ?? ''}
          aria-label=${tr(this, 'Edge label')}
          placeholder=${tr(this, 'Edge label')}
          @change=${(event: Event) =>
            this._setEdgeLabel(
              model,
              entity.entityId,
              (event.currentTarget as HTMLInputElement).value
            )}
          @keydown=${(event: KeyboardEvent) => {
            event.stopPropagation();
            if (event.isComposing) return;
            if (event.key === 'Enter') {
              event.preventDefault();
              (event.currentTarget as HTMLInputElement).blur();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              const input = event.currentTarget as HTMLInputElement;
              input.value = edge.label ?? '';
              input.blur();
            }
          }}
        />
        <button
          type="button"
          title=${tr(this, 'Delete edge')}
          @click=${() => this._deleteEdge(model, entity.entityId)}
        >
          ${tr(this, 'Delete')}
        </button>
      </div>
    `;
  }
  private _renderVisualDiagram(model: MermaidElementModel) {
    const renderState = getMermaidRenderStateSignal(model).value;
    const snapshot = renderState.interaction;
    const selectedId = getMermaidVirtualSelectionSignal(model).value;
    const visualState = getMermaidVisualEditingSignal(model).value;
    if (!snapshot) {
      this._ensureVisualRuntime(model).catch(console.error);
      return nothing;
    }
    const entity = selectedId
      ? (snapshot.entities.find(item => item.entityId === selectedId) ?? null)
      : null;
    const entityStyle = entity
      ? this._visualEntityStyle(model, snapshot, entity)
      : null;
    const inline =
      entity &&
      isMermaidInlineEditableEntityId(entity.entityId) &&
      visualState.inlineEntityId === entity.entityId &&
      entityStyle
        ? html`
            <textarea
              class="inline-node-editor"
              style=${styleMap(entityStyle)}
              .value=${this._inlineDraft}
              rows="2"
              spellcheck="false"
              aria-label=${tr(
                this,
                entity.kind === 'node'
                  ? 'Edit Mermaid node label'
                  : 'Edit Mermaid entity label'
              )}
              @pointerdown=${stopEvent}
              @click=${stopEvent}
              @input=${this._onInlineInput}
              @compositionstart=${this._onInlineCompositionStart}
              @compositionend=${this._onInlineCompositionEnd}
              @blur=${this._onInlineBlur}
              @keydown=${this._onInlineKeyDown}
            ></textarea>
          `
        : nothing;

    return html`
      <div
        class="visual-layer"
        role="group"
        aria-label=${tr(this, 'Mermaid visual editor')}
        @keydown=${this._onVisualKeyDown}
      >
        <div
          class="visual-hit-area"
          style=${styleMap(this._visualHitAreaStyle(model))}
          aria-hidden="true"
          @pointerdown=${stopEvent}
          @pointerup=${stopEvent}
          @click=${this._onVisualHitClick}
          @dblclick=${this._onVisualHitDoubleClick}
        ></div>
        ${
          entity && entityStyle
            ? html`<div
                class="virtual-selection"
                style=${styleMap(entityStyle)}
                aria-hidden="true"
              ></div>`
            : nothing
        }
        ${
          entity?.kind === 'node' && entityStyle
            ? this._renderNodeToolbar(model, entity, entityStyle)
            : nothing
        }
        ${
          entity?.kind === 'node' && entityStyle
            ? this._renderNodeAffordances(model, entity, entityStyle)
            : nothing
        }
        ${
          entity?.kind === 'edge'
            ? this._renderEdgeToolbar(model, snapshot, entity)
            : nothing
        }
        ${
          entity &&
          entityStyle &&
          entity.kind !== 'node' &&
          entity.kind !== 'edge'
            ? this._renderFamilyToolbar(model, entity, entityStyle)
            : nothing
        }
        ${
          entity &&
          entityStyle &&
          entity.kind !== 'node' &&
          entity.kind !== 'edge'
            ? this._renderFamilyConnectAffordance(model, entity, entityStyle)
            : nothing
        }
        ${this._renderConnectFeedback(model, snapshot, entity, entityStyle)}
        ${inline}
      </div>
    `;
  }

  private _setMode(mode: 'diagram' | 'code' | 'split') {
    const model = this._activeModel;
    if (!model || !this._isEditable(model)) return;
    if (mode === 'diagram') {
      if (this._canClose(model)) {
        showMermaidDiagram(this.gfx, model);
      }
      return;
    }

    const textarea = this._textarea;
    const selection = textarea
      ? {
          direction: textarea.selectionDirection,
          end: textarea.selectionEnd,
          start: textarea.selectionStart,
        }
      : null;

    setMermaidViewMode(model, mode);
    trackMermaidAction(this.std, 'edit-mode', mode);
    this.updateComplete
      .then(() => {
        const next = this._textarea;
        if (!next) return;
        next.focus();
        if (selection) {
          next.setSelectionRange(
            selection.start,
            selection.end,
            selection.direction
          );
        }
      })
      .catch(console.error);
  }

  private _repairWithAI(model: MermaidElementModel) {
    const provider = this.std.getOptional(MermaidActionProvider);
    const input = { elementId: model.id, source: model.source.toString() };
    if (!provider?.can('repair-ai', input)) return;
    Promise.resolve(provider.run('repair-ai', input)).catch(console.error);
  }

  private _revertLastValid(model: MermaidElementModel) {
    const lastValid = getMermaidRenderStateSignal(model).value.lastValidSource;
    if (lastValid === null || lastValid === model.source.toString()) return;
    replaceMermaidSource(model, lastValid);
  }

  private _statusLabel(model: MermaidElementModel) {
    const state = getMermaidRenderStateSignal(model).value;
    switch (state.status) {
      case 'ready':
      case 'degraded':
        return tr(this, 'Mermaid diagram is valid');
      case 'pending':
        return tr(this, 'Validating Mermaid');
      case 'invalid':
        return tr(this, 'Invalid Mermaid syntax');
      case 'error':
        return tr(this, 'Mermaid render error');
      case 'empty':
        return tr(this, 'Mermaid source is empty');
      default:
        return tr(this, 'Mermaid preview is waiting');
    }
  }

  override render() {
    const model = this._activeModel;
    if (!model || !this.gfx.selection.editing || !this._isEditable(model)) {
      return nothing;
    }

    const mode = getMermaidViewModeSignal(model).value;
    if (mode === 'diagram') {
      return this._renderVisualDiagram(model);
    }

    const renderState = getMermaidRenderStateSignal(model).value;
    const location = renderState.message
      ? getMermaidErrorLocation(renderState.message)
      : null;
    const [, , modelWidth, modelHeight] = model.deserializedXYWH;
    const { zoom, viewScale } = this.gfx.viewport;
    const [viewX, viewY] = this.gfx.viewport.toViewCoord(model.x, model.y);
    const style = {
      left: `${viewX}px`,
      top: `${viewY}px`,
      width: `${Math.max(320, modelWidth * zoom * viewScale)}px`,
      height: `${Math.max(220, modelHeight * zoom * viewScale)}px`,
    };
    const input = { elementId: model.id, source: model.source.toString() };
    const actionProvider = this.std.getOptional(MermaidActionProvider);
    const canRepair = actionProvider?.can('repair-ai', input) ?? false;
    const canRevert =
      renderState.lastValidSource !== null &&
      renderState.lastValidSource !== model.source.toString();
    const hasIssue =
      renderState.status === 'invalid' ||
      renderState.status === 'error' ||
      renderState.status === 'empty';

    return html`
      <section
        class="editor-surface"
        style=${styleMap(style)}
        role="region"
        aria-label=${tr(this, 'Mermaid source editor')}
        aria-describedby="mermaid-editor-status"
        @pointerdown=${stopEvent}
        @pointerup=${stopEvent}
        @click=${stopEvent}
        @keydown=${this._onKeyDown}
      >
        <header class="header">
          <div class="modes" role="group" aria-label=${tr(this, 'View mode')}>
            <button
              type="button"
              aria-pressed="false"
              @click=${() => this._setMode('diagram')}
            >
              ${tr(this, 'Diagram')}
            </button>
            <button
              type="button"
              aria-pressed=${String(mode === 'code')}
              @click=${() => this._setMode('code')}
            >
              ${tr(this, 'Code')}
            </button>
            <button
              type="button"
              aria-pressed=${String(mode === 'split')}
              @click=${() => this._setMode('split')}
            >
              ${tr(this, 'Split')}
            </button>
          </div>
          <div
            class="recovery"
            role="group"
            aria-label=${tr(this, 'Mermaid recovery actions')}
          >
            ${
              hasIssue
                ? html`
                    <button
                      type="button"
                      @click=${() => this._textarea?.focus()}
                    >
                      ${tr(this, 'Fix manually')}
                    </button>
                  `
                : nothing
            }
            ${
              hasIssue && canRepair
                ? html`
                    <button
                      type="button"
                      @click=${() => this._repairWithAI(model)}
                    >
                      ${tr(this, 'Repair with AI')}
                    </button>
                  `
                : nothing
            }
            ${
              hasIssue && canRevert
                ? html`
                    <button
                      type="button"
                      @click=${() => this._revertLastValid(model)}
                    >
                      ${tr(this, 'Revert to last valid')}
                    </button>
                  `
                : nothing
            }
          </div>
        </header>

        <div class="workspace ${mode}">
          <div class="code-pane">
            <textarea
              class="source-editor"
              .value=${this._source}
              spellcheck="false"
              aria-label=${tr(this, 'Mermaid source')}
              aria-describedby="mermaid-editor-status"
              aria-invalid=${String(hasIssue)}
              @input=${this._onInput}
              @compositionend=${this._onCompositionEnd}
              @copy=${stopEvent}
              @cut=${stopEvent}
              @paste=${stopEvent}
            ></textarea>
          </div>
          ${
            mode === 'split'
              ? html`
                  <div
                    class="preview"
                    role="img"
                    aria-label=${tr(this, 'Mermaid preview')}
                  >
                    ${
                      renderState.svg
                        ? unsafeHTML(renderState.svg)
                        : html`<div class="preview-placeholder">
                            ${tr(this, 'Mermaid preview is waiting')}
                          </div>`
                    }
                  </div>
                `
              : nothing
          }
        </div>

        <footer
          id="mermaid-editor-status"
          class="status ${renderState.status}"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <span>
            ${this._statusLabel(model)}
            ${
              location?.line
                ? html` · ${tr(this, 'Line')}
                  ${location.line}${
                    location.column ? html`:${location.column}` : nothing
                  }`
                : nothing
            }
          </span>
          ${
            renderState.message
              ? html`<pre class="technical" title=${renderState.message}>
                ${renderState.message}
              </pre>`
              : nothing
          }
        </footer>
      </section>
    `;
  }
}

export const mermaidEditorWidget = WidgetViewExtension(
  'affine:page',
  MERMAID_EDITOR_WIDGET,
  literal`${unsafeStatic(MERMAID_EDITOR_WIDGET)}`
);
